// Pagina de documentacion. Layout de 3 columnas tipo frameworks modernos
// (Vue/Next/Stripe docs):
//   ─ Sidebar izquierda: nav agrupada con search por nombre.
//   ─ Centro: markdown renderizado con syntax highlighting + anchor links.
//   ─ TOC derecha: index de headings del doc activo, con scroll-spy.
//
// El contenido viene del backend (`/api/v1/docs/:slug`), que lee de
// `packages/cli/docs/*.md` — un solo source of truth para CLI y web.

import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, FileText, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import {
  type DocTopicMeta,
  groupTopics,
  useDocContent,
  useDocsIndex,
} from '@/hooks/queries/use-docs';
import { cn } from '@/lib/utils';
// highlight.js theme — tema oscuro consistente con Monaco.
import 'highlight.js/styles/github-dark.css';

interface HeadingItem {
  level: 2 | 3;
  text: string;
  id: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

/** Extrae h2/h3 del markdown crudo via regex (suficiente para TOC simple). */
function extractHeadings(markdown: string): HeadingItem[] {
  const lines = markdown.split('\n');
  const out: HeadingItem[] = [];
  let inCodeBlock = false;
  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;
    const m2 = /^##\s+(.+)$/.exec(line);
    if (m2) {
      const text = m2[1].trim();
      out.push({ level: 2, text, id: slugify(text) });
      continue;
    }
    const m3 = /^###\s+(.+)$/.exec(line);
    if (m3) {
      const text = m3[1].trim();
      out.push({ level: 3, text, id: slugify(text) });
    }
  }
  return out;
}

/**
 * Encuentra el siguiente y anterior topic en el orden flat (sin agrupar).
 * Util para la nav prev/next al pie del contenido.
 */
function findAdjacentTopics(
  all: DocTopicMeta[] | undefined,
  currentSlug: string,
): { prev: DocTopicMeta | null; next: DocTopicMeta | null } {
  if (!all) return { prev: null, next: null };
  const flat = groupTopics(all).flatMap((g) => g.topics);
  const idx = flat.findIndex((t) => t.slug === currentSlug);
  if (idx < 0) return { prev: null, next: null };
  return {
    prev: idx > 0 ? flat[idx - 1] : null,
    next: idx < flat.length - 1 ? flat[idx + 1] : null,
  };
}

const DEFAULT_SLUG = 'index';

export function DocsPage(): React.ReactElement {
  const params = useParams({ strict: false }) as { slug?: string };
  const navigate = useNavigate();
  const slug = params.slug ?? DEFAULT_SLUG;

  const indexQuery = useDocsIndex();
  const contentQuery = useDocContent(slug);

  const [filter, setFilter] = useState('');
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const groups = useMemo(() => {
    const g = groupTopics(indexQuery.data?.topics);
    if (filter.trim().length === 0) return g;
    const needle = filter.trim().toLowerCase();
    return g
      .map((gr) => ({
        ...gr,
        topics: gr.topics.filter(
          (t) => t.title.toLowerCase().includes(needle) || t.slug.includes(needle),
        ),
      }))
      .filter((gr) => gr.topics.length > 0);
  }, [indexQuery.data, filter]);

  const headings = useMemo(
    () => (contentQuery.data ? extractHeadings(contentQuery.data.content) : []),
    [contentQuery.data],
  );

  const { prev, next } = useMemo(
    () => findAdjacentTopics(indexQuery.data?.topics, slug),
    [indexQuery.data, slug],
  );

  // Scroll-spy: track which heading is currently in view via IntersectionObserver.
  useEffect(() => {
    if (!contentQuery.data || headings.length === 0) return;
    const root = contentRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveHeading((entry.target as HTMLElement).id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0.1 },
    );
    const els = root.querySelectorAll<HTMLElement>('h2[id], h3[id]');
    for (const el of els) observer.observe(el);
    return () => observer.disconnect();
  }, [contentQuery.data, headings]);

  // Reset scroll al tope al cambiar de slug.
  useEffect(() => {
    contentRef.current?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0 });
  }, []);

  function selectTopic(s: string): void {
    void navigate({ to: '/docs/$slug', params: { slug: s } });
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] gap-0">
      {/* ─── Sidebar ─────────────────────────────────────────────── */}
      <aside className="hidden w-64 shrink-0 overflow-y-auto border-r border-border bg-card/30 px-3 py-4 md:block">
        <div className="mb-3 flex items-center gap-2 px-2">
          <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-semibold">Documentación</h2>
        </div>
        <div className="relative mb-3 px-2">
          <Search
            className="absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="text"
            placeholder="Buscar…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs"
          />
        </div>
        {indexQuery.isLoading && <p className="px-2 text-xs text-muted-foreground">Cargando…</p>}
        {indexQuery.isError && (
          <p className="px-2 text-xs text-destructive">No se pudo cargar el índice.</p>
        )}
        <nav className="space-y-4">
          {groups.map((g) => (
            <div key={g.group}>
              <h3 className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {g.group}
              </h3>
              <ul className="space-y-0.5">
                {g.topics.map((t) => {
                  const active = t.slug === slug;
                  return (
                    <li key={t.slug}>
                      <button
                        type="button"
                        onClick={() => selectTopic(t.slug)}
                        className={cn(
                          'w-full rounded-md px-2 py-1 text-left text-sm transition-colors',
                          active
                            ? 'bg-primary/15 font-medium text-primary'
                            : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                        )}
                      >
                        {t.title}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
          {groups.length === 0 && filter.length > 0 && (
            <p className="px-2 text-xs text-muted-foreground">Sin resultados.</p>
          )}
        </nav>
      </aside>

      {/* ─── Content ─────────────────────────────────────────────── */}
      <main
        id="docs-content"
        ref={contentRef}
        className="flex-1 overflow-y-auto px-6 py-6 lg:px-12"
      >
        <div className="mx-auto max-w-3xl">
          {contentQuery.isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {contentQuery.isError && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4">
              <p className="text-sm text-destructive">
                No se pudo cargar el documento.{' '}
                <button
                  type="button"
                  onClick={() => contentQuery.refetch()}
                  className="underline underline-offset-2"
                >
                  Reintentar
                </button>
              </p>
            </div>
          )}
          {contentQuery.data && (
            <article className="docs-prose">
              <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {contentQuery.data.group}
              </p>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[[rehypeHighlight, { ignoreMissing: true }]]}
                components={{
                  h1: ({ children }) => {
                    const text = String(children);
                    return (
                      <h1
                        id={slugify(text)}
                        className="mb-2 mt-1 text-3xl font-bold tracking-tight"
                      >
                        {children}
                      </h1>
                    );
                  },
                  h2: ({ children }) => {
                    const text = String(children);
                    return (
                      <h2
                        id={slugify(text)}
                        className="mb-3 mt-8 border-b border-border pb-2 text-xl font-semibold"
                      >
                        {children}
                      </h2>
                    );
                  },
                  h3: ({ children }) => {
                    const text = String(children);
                    return (
                      <h3 id={slugify(text)} className="mb-2 mt-6 text-base font-semibold">
                        {children}
                      </h3>
                    );
                  },
                  p: ({ children }) => (
                    <p className="mb-3 leading-relaxed text-foreground">{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul className="mb-3 ml-6 list-disc space-y-1 text-foreground">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="mb-3 ml-6 list-decimal space-y-1 text-foreground">{children}</ol>
                  ),
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  code: ({ className, children, ...props }) => {
                    const isBlock = (className ?? '').startsWith('language-');
                    if (isBlock) {
                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code
                        className="rounded border border-border/40 bg-muted/60 px-1 py-0.5 font-mono text-[0.85em]"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="mb-4 overflow-x-auto rounded-md border border-border bg-[#0d1117] p-4 text-xs leading-relaxed">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="mb-3 border-l-4 border-primary/40 bg-muted/30 px-4 py-2 italic text-muted-foreground">
                      {children}
                    </blockquote>
                  ),
                  a: ({ href, children }) => {
                    // Convertir links a otros docs (e.g. `flows.md`, `messages`)
                    // a navegacion interna de la docs page.
                    if (href && /^[a-z][a-z0-9-]*(\.md)?$/i.test(href)) {
                      const target = href.replace(/\.md$/i, '');
                      return (
                        <Link
                          to="/docs/$slug"
                          params={{ slug: target }}
                          className="text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          {children}
                        </Link>
                      );
                    }
                    return (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline underline-offset-2 hover:opacity-80"
                      >
                        {children}
                      </a>
                    );
                  },
                  table: ({ children }) => (
                    <div className="mb-4 overflow-x-auto">
                      <table className="w-full text-sm">{children}</table>
                    </div>
                  ),
                  th: ({ children }) => (
                    <th className="border-b border-border px-3 py-2 text-left font-semibold">
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td className="border-b border-border/30 px-3 py-2 align-top">{children}</td>
                  ),
                }}
              >
                {contentQuery.data.content}
              </ReactMarkdown>

              {/* Prev / Next nav */}
              <nav className="mt-12 flex items-center justify-between gap-3 border-t border-border pt-6">
                {prev ? (
                  <Button
                    variant="outline"
                    onClick={() => selectTopic(prev.slug)}
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Anterior
                      </div>
                      <div className="text-sm">{prev.title}</div>
                    </div>
                  </Button>
                ) : (
                  <span />
                )}
                {next ? (
                  <Button
                    variant="outline"
                    onClick={() => selectTopic(next.slug)}
                    className="flex items-center gap-2"
                  >
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        Siguiente
                      </div>
                      <div className="text-sm">{next.title}</div>
                    </div>
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <span />
                )}
              </nav>
            </article>
          )}
        </div>
      </main>

      {/* ─── TOC right ──────────────────────────────────────────── */}
      <aside className="hidden w-56 shrink-0 overflow-y-auto border-l border-border px-4 py-6 xl:block">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          En esta página
        </h3>
        {headings.length === 0 ? (
          <p className="text-xs text-muted-foreground">Sin secciones.</p>
        ) : (
          <ul className="space-y-1 text-xs">
            {headings.map((h) => (
              <li key={h.id} className={h.level === 3 ? 'pl-3' : ''}>
                <a
                  href={`#${h.id}`}
                  className={cn(
                    'block py-0.5 transition-colors',
                    activeHeading === h.id
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {h.text}
                </a>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}
